import { EraQL, Expression } from '@cotera/era';
import { err, ok, Result } from 'neverthrow';
import {
  BUILTIN_FUNCTION_IDENTIFIERS,
  StackFrameDef,
  ToolSymbolMap,
} from './types';
import _ from 'lodash';

export class SuspendPoint {
  constructor(
    private readonly params: {
      readonly def: StackFrameDef;
      readonly args: readonly Expression[];
      readonly idx: number;
    }
  ) {}

  get def() {
    return this.params.def;
  }

  get args() {
    return this.params.args;
  }

  get idx() {
    return this.params.idx;
  }
}

export type SuspendingError =
  | {
      readonly t: 'parse-errors';
      readonly errs: readonly EraQL.ParseError[];
    }
  | {
      readonly t: 'max-iterations-exceeded';
    };

export type EvalResult =
  | { readonly t: 'success'; readonly val: Expression }
  | { readonly t: 'error'; readonly err: SuspendingError }
  | {
      readonly t: 'continue';
      readonly suspendedOn: readonly SuspendPoint[];
      readonly continue: (vals: readonly Expression[]) => EvalResult;
    };

export const evalSuspendable = (params: {
  eraql: string;
  attributes: Record<string, Expression>;
  tools: ToolSymbolMap;
  bindings?: EraQL.Bindings;
}): EvalResult => {
  const { eraql, attributes, tools, bindings } = params;

  const tryEval = (ffiResults: Expression[]): EvalResult => {
    let ffiResultsIdx = 0;

    const doFfi =
      (def: StackFrameDef): EraQL.SpecialForm =>
      (args, ctx, func) => {
        const expr = ffiResults[ffiResultsIdx];

        if (expr !== undefined) {
          ffiResultsIdx++;
          return ok({ item: { t: 'expr', expr } });
        } else {
          const evaledArgs = EraQL.c.ListOf(EraQL.c.Expr)(
            [{ t: 'list', elems: args }, func[1]],
            ctx
          );

          if (evaledArgs.isErr()) {
            return err(evaledArgs.error);
          }

          const e: EraQL.ParseError = {
            t: 'interrupt',
            val: new SuspendPoint({
              def,
              args: evaledArgs.value.map(([expr]) => expr),
              idx: ffiResultsIdx,
            }),
          };

          return err([[e, func[1]]]);
        }
      };

    const builtins: EraQL.Bindings = Object.fromEntries(
      BUILTIN_FUNCTION_IDENTIFIERS.map((name) => [
        name,
        { t: 'special-form', sp: doFfi({ t: 'builtin', name }) },
      ])
    );

    const toolBindings: EraQL.Bindings = _.mapValues(tools, (def) => ({
      t: 'special-form' as const,
      sp: doFfi({ t: 'tool', id: def.toolId }),
    }));

    const parseRes = EraQL.parseExpr(eraql, {
      bindings: { ...toolBindings, ...builtins, ...bindings },
      attributes,
      eager: true,
    });

    if (parseRes.isOk()) {
      return { t: 'success', val: parseRes.value };
    }

    const [interrupts, otherErrors] = _.partition(
      parseRes.error.map(([err, _meta]) => err),
      (x) => x.t === 'interrupt'
    );

    if (otherErrors.length > 0) {
      return {
        t: 'error',
        err: { t: 'parse-errors', errs: otherErrors },
      };
    }

    const suspendedOn: SuspendPoint[] = interrupts.map(({ val }) => {
      if (!(val instanceof SuspendPoint)) {
        throw new Error(
          `Logic error attempted to suspend on ${JSON.stringify(val)}`
        );
      }

      return val;
    });

    return {
      t: 'continue',
      suspendedOn,
      continue: (exprs) => tryEval([...ffiResults, ...exprs]),
    };
  };

  return tryEval([]);
};

export const runSuspendable = async <E>(params: {
  eraql: string;
  attributes: Record<string, Expression>;
  tools: ToolSymbolMap;
  onInterrupt: (interrupt: SuspendPoint) => Promise<Result<Expression, E>>;
  bindings?: EraQL.Bindings;
  maxIterations?: number;
}): Promise<
  Result<
    Expression,
    SuspendingError | { readonly t: 'custom'; readonly err: E }
  >
> => {
  const { maxIterations = 10000, onInterrupt, ...rest } = params;

  let iter = 0;
  let curr: EvalResult = evalSuspendable(rest);

  while (curr.t === 'continue') {
    if (iter > maxIterations) {
      return err({ t: 'max-iterations-exceeded' });
    }

    iter++;

    const promises = curr.suspendedOn.map((point) => onInterrupt(point));
    const result = Result.combine(await Promise.all(promises));

    if (result.isErr()) {
      return err({ t: 'custom', err: result.error });
    }

    curr = curr.continue(result.value);
  }

  return curr.t === 'success' ? ok(curr.val) : err(curr.err);
};
